import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';

import type { DashboardComment } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { useUserContext } from 'src/components/app/context/UserContext';
import useFollowUpFilters from 'src/components/followUp/hooks/useFollowUpFilters';

const useHRQSCommentsQuery = (
  source: 'hrqs' | 'topics' | 'token',
  topics: Array<string>,
  currentPage: number,
  textFilter: string,
  widgetId?: number,
) => {
  const { i18n } = useTranslation();

  const { currentSurvey } = useUserContext();

  const endpoint = useMemo(
    () =>
      generatePath(apiPaths.WIDGET_DATA.OPEN_COMMENTS.COMMENTS, {
        surveyId: currentSurvey?.id ?? 0,
        widgetId: widgetId ?? 0,
      }),
    [apiPaths, currentSurvey?.id],
  );
  const { dateRange, followUpFilters, demographicsFilters, orgUnit } =
    useFollowUpFilters();

  const filtersToUse = useMemo(
    () => ({
      orgUnit,
      ...dateRange,
      ...followUpFilters,
      ...demographicsFilters,
      topicIds: topics.toString(),
      textFilter,
      page: currentPage,
    }),
    [
      orgUnit,
      followUpFilters,
      demographicsFilters,
      dateRange,
      topics,
      textFilter,
    ],
  );
  return useQuery<[DashboardComment[], number], AxiosError>({
    enabled:
      source === 'hrqs' && !!topics.length && !!widgetId && !!currentSurvey,
    queryKey: [endpoint, i18n.language, filtersToUse, topics],
    queryFn: ({ signal }) =>
      HttpService.get(endpoint, filtersToUse, undefined, {
        signal,
      }).then(
        ({
          data,
        }: AxiosResponse<{
          data: Array<DashboardComment>;
          pagination: {
            totalPages: number;
          };
        }>) =>
          Promise.resolve([data.data, data.pagination.totalPages] as [
            DashboardComment[],
            number,
          ]),
      ),
  });
};

export default useHRQSCommentsQuery;
